import React, { createContext, useContext, useEffect } from "react";
import { useRolePermissionsController, type PermissionContextValue } from "@Front/Stores/hooks/useRolePermissionsController";
import { useNotaryOfficeAffiliationsLoadEffect } from "@Front/Hooks/useNotaryOfficeAffiliationsLoadEffect";
import type { MatrixPermissionCanFn } from "@Front/Utils/permissionCanTypes";
import {
	registerNotaryPermissionsRefresh,
	unregisterNotaryPermissionsRefresh,
} from "@Front/Utils/notaryEntitlementRefreshBridge";

const RolePermissionsContext = createContext<PermissionContextValue>({
	can: () => false,
	loading: true,
	error: null,
	matrix: null,
	permissionContext: null,
	refresh: async () => undefined,
});

function NotaryOfficeAffiliationsBootstrap(): null {
	const { permissionContext, loading } = useRolePermissions();
	useNotaryOfficeAffiliationsLoadEffect(permissionContext, loading);
	return null;
}

function NotaryPermissionsRefreshBridgeRegistration(): null {
	const { refresh, permissionContext } = useRolePermissions();
	useEffect(() => {
		registerNotaryPermissionsRefresh(refresh, permissionContext?.userId ?? null);
		return (): void => {
			unregisterNotaryPermissionsRefresh();
		};
	}, [refresh, permissionContext?.userId]);
	return null;
}

export function RolePermissionsProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const value = useRolePermissionsController();
	return (
		<RolePermissionsContext.Provider value={value}>
			<NotaryPermissionsRefreshBridgeRegistration />
			<NotaryOfficeAffiliationsBootstrap />
			{children}
		</RolePermissionsContext.Provider>
	);
}

export function useRolePermissions(): PermissionContextValue {
	return useContext(RolePermissionsContext);
}

export type RolePermissionContext = PermissionContextValue["permissionContext"];

export function useRolePermissionContext(): RolePermissionContext {
	return useRolePermissions().permissionContext;
}

export function useRolePermissionsLoading(): boolean {
	return useRolePermissions().loading;
}

export function useRolePermissionsRefresh(): PermissionContextValue["refresh"] {
	return useRolePermissions().refresh;
}

export function useRolePermissionsCan(): MatrixPermissionCanFn {
	return useRolePermissions().can;
}
